import type { PaymentMethod, RecordQuery, RecordStatus, SortDir, SortField } from './types';
import { PAYMENT_METHODS, RECORD_STATUSES } from './types';

export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/** What the filter bar holds. Dates are `yyyy-MM-dd` or ''. */
export interface RecordFilters {
  search: string;
  contractorId: string;
  status: '' | RecordStatus;
  trackable: '' | 'yes' | 'no';
  method: '' | PaymentMethod;
  /** Payment Date: one exact day. */
  paymentDate: string;
  /** Date Range: payment date from / to. */
  rangeFrom: string;
  rangeTo: string;
  /** Select Service Period: the record's period must overlap this range. */
  serviceFrom: string;
  serviceTo: string;
}

export const NO_FILTERS: RecordFilters = {
  search: '', contractorId: '', status: '', trackable: '', method: '', paymentDate: '', rangeFrom: '', rangeTo: '', serviceFrom: '', serviceTo: '',
};

export interface ListState {
  filters: RecordFilters;
  page: number;
  pageSize: number;
  sort: SortField;
  dir: SortDir;
}

export const DEFAULT_SORT: { sort: SortField; dir: SortDir } = { sort: 'paymentDate', dir: 'desc' };

export function filtersActive(f: RecordFilters): boolean {
  return (Object.keys(NO_FILTERS) as (keyof RecordFilters)[]).some((k) => f[k] !== '');
}

/** Payment Date (one day) and Date Range (from/to) both filter the payment date; both must hold, so the effective range is their intersection. */
export function paymentDateBounds(f: RecordFilters): { from?: string; to?: string; empty: boolean } {
  const froms = [f.paymentDate, f.rangeFrom].filter(Boolean);
  const tos = [f.paymentDate, f.rangeTo].filter(Boolean);
  const from = froms.length ? froms.reduce((a, b) => (a > b ? a : b)) : undefined;
  const to = tos.length ? tos.reduce((a, b) => (a < b ? a : b)) : undefined;
  return { ...(from ? { from } : {}), ...(to ? { to } : {}), empty: from !== undefined && to !== undefined && from > to };
}

/** Builds the server query, or null when the filters cannot match anything (so no request is needed and the server is never sent a from-after-to range). */
export function toRecordQuery(state: ListState, year?: number): RecordQuery | null {
  const f = state.filters;
  const bounds = paymentDateBounds(f);
  if (bounds.empty) return null;
  if (f.serviceFrom && f.serviceTo && f.serviceFrom > f.serviceTo) return null;
  const q: RecordQuery = { page: state.page, pageSize: state.pageSize, sort: state.sort, dir: state.dir };
  if (f.search.trim()) q.search = f.search.trim().slice(0, 100);
  if (f.contractorId) q.contractorId = f.contractorId;
  if (f.status) q.status = f.status;
  if (f.method) q.paymentMethod = f.method;
  if (f.trackable) q.form1099Trackable = f.trackable === 'yes';
  if (bounds.from) q.paymentDateFrom = bounds.from;
  if (bounds.to) q.paymentDateTo = bounds.to;
  if (f.serviceFrom) q.servicePeriodFrom = f.serviceFrom;
  if (f.serviceTo) q.servicePeriodTo = f.serviceTo;
  if (year !== undefined) q.year = year;
  return q;
}

export function queryString(q: Partial<RecordQuery>, omitPaging = false): string {
  const p = new URLSearchParams();
  for (const [key, value] of Object.entries(q)) {
    if (value === undefined || value === '') continue;
    if (omitPaging && (key === 'page' || key === 'pageSize')) continue;
    p.set(key, String(value));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

// ---- URL <-> state: the list keeps its filters in the address, so "Back to payment list" restores them ----
const SORTS: readonly SortField[] = ['paymentDate', 'contractor', 'servicePeriod', 'paymentMethod', 'amount', 'form1099', 'status'];
const ISO = /^\d{4}-\d{2}-\d{2}$/;

export function parseListState(params: URLSearchParams): ListState {
  const get = (k: string) => params.get(k) ?? '';
  const date = (k: string) => (ISO.test(get(k)) ? get(k) : '');
  const status = get('status');
  const method = get('method');
  const trackable = get('trackable');
  const sort = get('sort') as SortField;
  const pageSize = Number(get('size'));
  const page = Number(get('page'));
  return {
    filters: {
      search: get('q').slice(0, 100),
      contractorId: get('contractor'),
      status: (RECORD_STATUSES as readonly string[]).includes(status) ? (status as RecordStatus) : '',
      trackable: trackable === 'yes' || trackable === 'no' ? trackable : '',
      method: (PAYMENT_METHODS as readonly string[]).includes(method) ? (method as PaymentMethod) : '',
      paymentDate: date('paid'), rangeFrom: date('from'), rangeTo: date('to'), serviceFrom: date('sfrom'), serviceTo: date('sto'),
    },
    page: Number.isInteger(page) && page >= 1 ? page : 1,
    pageSize: (PAGE_SIZE_OPTIONS as readonly number[]).includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
    sort: SORTS.includes(sort) ? sort : DEFAULT_SORT.sort,
    dir: get('dir') === 'asc' ? 'asc' : get('dir') === 'desc' ? 'desc' : sort && SORTS.includes(sort) && sort !== 'paymentDate' ? 'asc' : DEFAULT_SORT.dir,
  };
}

export function listStateToParams(s: ListState): URLSearchParams {
  const p = new URLSearchParams();
  const set = (k: string, v: string) => { if (v) p.set(k, v); };
  set('q', s.filters.search); set('contractor', s.filters.contractorId); set('status', s.filters.status); set('trackable', s.filters.trackable);
  set('method', s.filters.method); set('paid', s.filters.paymentDate); set('from', s.filters.rangeFrom); set('to', s.filters.rangeTo);
  set('sfrom', s.filters.serviceFrom); set('sto', s.filters.serviceTo);
  if (s.page > 1) p.set('page', String(s.page));
  if (s.pageSize !== DEFAULT_PAGE_SIZE) p.set('size', String(s.pageSize));
  if (s.sort !== DEFAULT_SORT.sort || s.dir !== DEFAULT_SORT.dir) { p.set('sort', s.sort); p.set('dir', s.dir); }
  return p;
}

/** The order a header click moves to: first click = ascending (newest first for the payment date), second = the other way, third = back to the default. */
export function nextSort(current: { sort: SortField; dir: SortDir }, field: SortField): { sort: SortField; dir: SortDir } {
  if (current.sort !== field) return { sort: field, dir: field === 'paymentDate' ? 'desc' : 'asc' };
  const first: SortDir = field === 'paymentDate' ? 'desc' : 'asc';
  if (current.dir === first) return { sort: field, dir: first === 'asc' ? 'desc' : 'asc' };
  return { ...DEFAULT_SORT };
}
